import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import AccommodationDetailsPage from '../../pages/AccomodationDetailsPage';
import { getPropertyDetail } from '../../server/getPropertyDetail';
import { getMapDataByDestination } from '../../server/getMapDataByDestination';
import { destinationMiddleware } from '../../server/middleware/destinationMiddleware';
import { sanitizeHtml } from '../../server/utlis/sanitizeHtml';
import { generateLodgingBusinessSchema, generateBreadcrumbSchema } from '../../utils/jsonLd';

const loadDestination = createServerFn()
    .middleware([destinationMiddleware])
    .handler(async ({ context }) => {
        return context.destination;
    });

export const Route = createFileRoute('/accommodation/$slug')({
    validateSearch: (search) => ({
        intent: search.intent === 'book' || search.intent === 'quotes' ? search.intent : undefined,
        enquiry: search.enquiry,
        ref: search.ref,
    }),
    beforeLoad: async () => {
        try {
            const destination = await loadDestination();
            return { destination };
        } catch (error) {
            console.error('Failed to load destination:', error);
            throw error;
        }
    },
    loader: async ({ params, context }) => {
        const { slug } = params;
        const propertiesData = await getPropertyDetail({ data: { slug } }).catch(() => []);
        let property = propertiesData?.[0] || null;

        // The CMS often stores this rich-text field with "&nbsp;" between
        // every word instead of a normal space, which blocks the browser
        // from wrapping at all — sanitizeHtml() also fixes that up.
        if (property?.fullDescription) {
            const fullDescription = await sanitizeHtml({
                data: { html: property.fullDescription },
            }).catch(() => property.fullDescription);
            property = { ...property, fullDescription };
        }

        // `property.visibleOn.destination` is only a partial reference
        // ({_id, name}) — use the full destination doc from beforeLoad
        // (same one every other page uses) when the site's canonical
        // `.url` is needed, e.g. for `head()`'s og:url below.
        const destinationId = property?.visibleOn?.destination?._id || null;

        const markerData = destinationId
            ? await getMapDataByDestination({ data: { destinationId } }).catch(() => [])
            : [];

        return {
            property,
            markerData,
            destination: context.destination,
        };
    },
    // Server-rendered (via TanStack Router's own <HeadContent /> in
    // __root.jsx) — unlike react-helmet-async, which only patches
    // document.head after client-side hydration, so non-JS crawlers/link
    // unfurlers (Slack, WhatsApp, Twitter/X, Facebook, LinkedIn) never saw
    // it. metaData.title/description are always populated by the backend.
    head: ({ loaderData, params }) => {
        const property = loaderData?.property;
        if (!property) return {};

        const title = property.metaData?.title || property.title;
        const description =
            property.metaData?.description || property.briefDescription;
        const image = property.photos?.[0]?.url;
        const siteUrl = (loaderData?.destination?.url || 'https://www.maasaimara.com').replace(/\/$/, '');
        const canonicalUrl = `${siteUrl}/accommodation/${params.slug}`;

        const lodgingSchema = generateLodgingBusinessSchema(property, canonicalUrl);
        const breadcrumbSchema = generateBreadcrumbSchema(
            [
                { title: 'Home', url: '/' },
                { title: 'Accommodations', url: '/accommodation' },
                { title: property.title, url: `/accommodation/${params.slug}` },
            ],
            siteUrl
        );

        return {
            meta: [
                { title },
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: canonicalUrl },
                ...(image
                    ? [
                        { property: 'og:image', content: image },
                        { property: 'og:image:type', content: 'image/jpeg' },
                    ]
                    : []),
            ],
            links: [
                { rel: 'canonical', href: canonicalUrl },
            ],
            scripts: [
                ...(lodgingSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(lodgingSchema) }]
                    : []),
                ...(breadcrumbSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(breadcrumbSchema) }]
                    : []),
            ],
        };
    },
    component: AccommodationDetailsPage,
    // ── Caching config
    staleTime: 5 * 60 * 1000,   // reuse cached data for 5 mins on re-navigation
    gcTime: 10 * 60 * 1000,  // keep in memory for 10 mins after route unmounts
});